import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import API from '../../../../api/api'
import { INTEGRATION_TYPES, getCompanyInitialValues, getCompanyValidationSchema } from '../helpers'

const OrganisationIntegrationModal = ({ type, onClose = () => {}, onCreated = () => {} }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const [isLoading, setIsLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const onSubmit = async (values) => {
    setIsLoading(true)
    try {
      const { data } = await API.createPMCompany(type, values)
      displaySnackbar(t('organisation:places.integrations.companyModal.toasts.created'))
      onCreated(data)
      onClose()
    } catch ({ response }) {
      const { data } = response
      displaySnackbar(
        data && data.message
          ? data.message
          : t('organisation:places.integrations.companyModal.toasts.createFailed'),
        true,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const renderForm = ({ handleChange, values }) => {
    switch (type) {
      case INTEGRATION_TYPES.YARDI:
        return (
          <React.Fragment>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.name.label')}
              </span>
              <TextField
                name="name"
                value={values.name}
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.name.placeholder',
                )}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.username.label')}
              </span>
              <TextField
                name="username"
                value={values.username}
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.username.placeholder',
                )}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.password.label')}
              </span>
              <TextField
                name="password"
                type="password"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.password.placeholder',
                )}
                value={values.password}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.serverName.label')}
              </span>
              <TextField
                name="serverName"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.serverName.placeholder',
                )}
                value={values.serverName}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.database.label')}
              </span>
              <TextField
                name="database"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.database.placeholder',
                )}
                value={values.database}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.url.label')}
              </span>
              <TextField
                name="url"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.url.placeholder',
                )}
                value={values.url}
                onChange={handleChange}
              />
            </div>
          </React.Fragment>
        )
      case INTEGRATION_TYPES.ENTRATA:
        return (
          <React.Fragment>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.name.label')}
              </span>
              <TextField
                name="name"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.name.placeholder',
                )}
                value={values.name}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.managementCompany.label')}
              </span>
              <TextField
                name="managementCompany"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.managementCompany.placeholder',
                )}
                onChange={handleChange}
                value={values.managementCompany}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.entrataUsername.label')}
              </span>
              <TextField
                name="username"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.entrataUsername.placeholder',
                )}
                value={values.username}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.password.label')}
              </span>
              <TextField
                name="password"
                type="password"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.password.placeholder',
                )}
                value={values.password}
                onChange={handleChange}
              />
            </div>
          </React.Fragment>
        )
      case INTEGRATION_TYPES.REAL_PAGE:
        return (
          <React.Fragment>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.name.label')}
              </span>
              <TextField
                name="name"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.name.placeholder',
                )}
                value={values.name}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.pmcid.label')}
              </span>
              <TextField
                name="pmcid"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.pmcid.placeholder',
                )}
                value={values.pmcid}
                onChange={handleChange}
              />
            </div>
          </React.Fragment>
        )
      case INTEGRATION_TYPES.RENT_MANAGER:
        return (
          <React.Fragment>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.name.label')}
              </span>
              <TextField
                name="name"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.name.placeholder',
                )}
                value={values.name}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.username.label')}
              </span>
              <TextField
                name="username"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.username.placeholder',
                )}
                value={values.username}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.password.label')}
              </span>
              <TextField
                name="password"
                type="password"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.password.placeholder',
                )}
                value={values.password}
                onChange={handleChange}
              />
            </div>
            <div>
              <span className="input-label">
                {t('organisation:places.integrations.companyModal.fields.companyCode.label')}
              </span>
              <TextField
                name="companyCode"
                placeholder={t(
                  'organisation:places.integrations.companyModal.fields.companyCode.placeholder',
                )}
                value={values.companyCode}
                onChange={handleChange}
              />
            </div>
          </React.Fragment>
        )
      default:
        return null
    }
  }

  renderForm.propTypes = {
    handleChange: PropTypes.func,
    values: PropTypes.object,
  }

  return (
    <ModalContainer
      additionalClass="ctp-integration-modal"
      onClose={onClose}
      headerTitle={t('organisation:places.integrations.companyModal.headerTitle')}
    >
      <Formik
        enableReinitialize
        initialValues={getCompanyInitialValues(type)}
        validationSchema={getCompanyValidationSchema(type)}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isValid, ...props }) => (
          <React.Fragment>
            <form className="integration-modal-content">{renderForm(props)}</form>
            <div className="integration-modal-actions">
              <button onClick={onClose} type="button" className="button button-gray">
                {t('common:actions.cancel')}
              </button>
              <button
                className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
                type="button"
                disabled={!isValid}
                onClick={handleSubmit}
              >
                {t('organisation:places.integrations.companyModal.addButton')}
              </button>
            </div>
          </React.Fragment>
        )}
      </Formik>
    </ModalContainer>
  )
}

OrganisationIntegrationModal.propTypes = {
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
  type: PropTypes.oneOf(Object.values(INTEGRATION_TYPES)).isRequired,
}

export default OrganisationIntegrationModal
